import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TranslatorContext } from "../../../context/Translator";
import { Row, Col, Card, Modal, Form, Button, ButtonGroup, Badge, Pagination, Table, Alert } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import { FiEye, FiPrinter, FiEdit, FiTrash2, FiCalendar, FiList, FiPlus, FiClock, FiUser, FiPhone, FiMail, FiMapPin, FiFileText, FiChevronDown, FiSearch, FiX, FiPackage, FiDollarSign, FiShoppingCart, FiCreditCard } from "react-icons/fi";
import { FaPrint } from "react-icons/fa";
import Swal from 'sweetalert2';

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
};

// Status options for orders (updated with confirmed and on_the_way)
const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending', color: '#ffc107', bg: 'warning' },
    { value: 'confirmed', label: 'Confirmed', color: '#0d6efd', bg: 'primary' },
    { value: 'processing', label: 'Processing', color: '#0dcaf0', bg: 'info' },
    { value: 'on_the_way', label: 'On The Way', color: '#6f42c1', bg: 'purple' },
    { value: 'delivered', label: 'Delivered', color: '#198754', bg: 'success' },
    { value: 'cancelled', label: 'Cancelled', color: '#dc3545', bg: 'danger' }
];

// Status Badge Component
const StatusBadge = ({ status, onStatusChange, orderId }) => {
    const [updating, setUpdating] = useState(false);
    const [showStatusOptions, setShowStatusOptions] = useState(false);
    const currentStatus = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];

    const handleStatusChange = async (newStatus) => {
        if (newStatus === status) {
            setShowStatusOptions(false);
            return;
        }

        setUpdating(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/orders/status/${orderId}`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.status) {
                onStatusChange(orderId, newStatus);
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Status updated successfully',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000
                });
            }
        } catch (error) {
            console.error('Error updating status:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update status',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setUpdating(false);
            setShowStatusOptions(false);
        }
    };

    // Custom styling for purple bg
    const getVariantClass = (bg) => {
        if (bg === 'purple') return 'dark';
        return bg;
    };

    return (
        <div style={{ position: 'relative' }}>
            <Button
                variant={currentStatus.bg === 'purple' ? 'dark' : currentStatus.bg}
                size="sm"
                disabled={updating}
                onClick={() => setShowStatusOptions(!showStatusOptions)}
                className="d-inline-flex align-items-center gap-1"
                style={currentStatus.bg === 'purple' ? { backgroundColor: '#6f42c1', borderColor: '#6f42c1' } : {}}
            >
                {currentStatus.label}
                <FiChevronDown size={12} />
                {updating && <span className="spinner-border spinner-border-sm ms-1" />}
            </Button>

            {showStatusOptions && (
                <div className="status-options-popup" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    minWidth: '120px',
                    marginTop: '4px'
                }}>
                    {STATUS_OPTIONS.map(option => (
                        <Button
                            key={option.value}
                            variant="light"
                            size="sm"
                            onClick={() => handleStatusChange(option.value)}
                            className="d-block w-100 text-start border-0"
                            style={{
                                backgroundColor: status === option.value ? option.color : 'transparent',
                                color: status === option.value ? 'white' : 'inherit'
                            }}
                        >
                            {option.label}
                        </Button>
                    ))}
                </div>
            )}
        </div>
    );
};

// Payment Modal Component - Updated with existing payments display and update prevention
const PaymentModal = ({ show, onClose, order, onPaymentSuccess, paymentMethods }) => {
    const { t } = useContext(TranslatorContext);
    const [payments, setPayments] = useState([{
        payment_method_id: '',
        amount: '',
        payment_date: new Date().toISOString().split('T')[0]
    }]);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    // Calculate existing total paid from order payments
    const getExistingTotalPaid = () => {
        if (!order?.payments) return 0;
        return order.payments.reduce((total, payment) => {
            return total + (parseFloat(payment.amount) || 0);
        }, 0);
    };

    // Calculate total paid including new payments
    const calculateTotalPaid = () => {
        const existingPaid = getExistingTotalPaid();
        const newPaymentsTotal = payments.reduce((total, payment) => {
            return total + (parseFloat(payment.amount) || 0);
        }, 0);
        return existingPaid + newPaymentsTotal;
    };

    // Check if order is fully paid
    const isFullyPaid = () => {
        const orderTotal = parseFloat(order?.total_amount || 0);
        const totalPaid = getExistingTotalPaid();
        return totalPaid >= orderTotal && orderTotal > 0;
    };

    // Calculate remaining amount (considering existing payments)
    const calculateRemainingAmount = () => {
        const orderTotal = parseFloat(order?.total_amount || 0);
        const existingPaid = getExistingTotalPaid();
        const newPaymentsTotal = payments.reduce((total, payment) => {
            return total + (parseFloat(payment.amount) || 0);
        }, 0);
        const totalPaid = existingPaid + newPaymentsTotal;
        return Math.max(0, orderTotal - totalPaid);
    };

    // Calculate total of new payments only
    const calculateNewPaymentsTotal = () => {
        return payments.reduce((total, payment) => {
            return total + (parseFloat(payment.amount) || 0);
        }, 0);
    };

    const addPaymentRow = () => {
        if (isFullyPaid()) return; // Don't allow adding if already fully paid

        const remainingAmount = calculateRemainingAmount();
        const newPayment = {
            payment_method_id: '',
            amount: remainingAmount > 0 ? remainingAmount : '',
            payment_date: new Date().toISOString().split('T')[0]
        };
        setPayments([...payments, newPayment]);
    };

    const removePaymentRow = (index) => {
        if (payments.length > 1) {
            const newPayments = payments.filter((_, i) => i !== index);
            setPayments(newPayments);
        }
    };

    const updatePayment = (index, field, value) => {
        const newPayments = [...payments];
        newPayments[index][field] = value;
        setPayments(newPayments);

        if (errors[`payments.${index}.${field}`]) {
            const newErrors = { ...errors };
            delete newErrors[`payments.${index}.${field}`];
            setErrors(newErrors);
        }
    };

    const validatePayments = () => {
        const newErrors = {};

        payments.forEach((payment, index) => {
            if (payment.amount && parseFloat(payment.amount) > 0) {
                if (!payment.payment_method_id) {
                    newErrors[`payments.${index}.payment_method_id`] = 'Payment method is required';
                }
                if (!payment.amount || payment.amount <= 0) {
                    newErrors[`payments.${index}.amount`] = 'Valid amount is required';
                }
                if (!payment.payment_date) {
                    newErrors[`payments.${index}.payment_date`] = 'Payment date is required';
                }
            }
        });

        const totalPaid = calculateTotalPaid();
        const orderTotal = parseFloat(order?.total_amount || 0);

        if (totalPaid > orderTotal) {
            newErrors['total_exceeds'] = `Total payment (${totalPaid}) cannot exceed order total (${orderTotal})`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        // Filter out empty payment rows
        const validPayments = payments.filter(payment =>
            payment.amount && parseFloat(payment.amount) > 0
        );

        if (validPayments.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Warning',
                text: 'Please add at least one payment',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2000
            });
            return;
        }

        if (!validatePayments()) {
            return;
        }

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/orders/payment/${order.id}`,
                { payments: validPayments },
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data.status) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Payments added successfully',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000
                });

                onPaymentSuccess();
                onClose();
            } else {
                throw new Error(response.data.message || 'Failed to add payments');
            }
        } catch (error) {
            console.error('Error adding payments:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to add payments',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleSetFullAmount = (index) => {
        const remainingAmount = calculateRemainingAmount();
        if (remainingAmount > 0) {
            updatePayment(index, 'amount', remainingAmount);
        }
    };

    // Reset form when modal opens
    useEffect(() => {
        if (show && order) {
            const remainingAmount = calculateRemainingAmount();
            setPayments([{
                payment_method_id: '',
                amount: remainingAmount > 0 && !isFullyPaid() ? remainingAmount : '',
                payment_date: new Date().toISOString().split('T')[0]
            }]);
            setErrors({});
        }
    }, [show, order]);

    const isSubmitDisabled = () => {
        if (isFullyPaid()) return true;

        const hasValidPayments = payments.some(payment =>
            payment.amount && parseFloat(payment.amount) > 0
        );

        const hasInvalidPayments = payments.some(payment => {
            if (payment.amount && parseFloat(payment.amount) > 0) {
                return !payment.payment_method_id || !payment.payment_date;
            }
            return false;
        });

        const totalPaid = calculateTotalPaid();
        const orderTotal = parseFloat(order?.total_amount || 0);

        return !hasValidPayments || hasInvalidPayments || totalPaid > orderTotal;
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            <Modal.Header closeButton className="p-2">
                <Modal.Title>
                    <FiCreditCard className="me-2" />
                    Payment Details - Order #{order?.order_number}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Order Summary Cards */}
                <Row className="mb-3">
                    <Col md={4}>
                        <Alert variant="info" className="mb-0">
                            <small className="text-muted">Order Total</small>
                            <h4 className="mb-0 text-primary">
                                {parseFloat(order?.total_amount || 0)}
                            </h4>
                        </Alert>
                    </Col>
                    <Col md={4}>
                        <Alert variant="secondary" className="mb-0">
                            <small className="text-muted">Already Paid</small>
                            <h4 className="mb-0 text-secondary">
                                {getExistingTotalPaid()}
                            </h4>
                        </Alert>
                    </Col>
                    <Col md={4}>
                        <Alert variant={isFullyPaid() ? "success" : "warning"} className="mb-0">
                            <small className="text-muted">Remaining</small>
                            <h4 className={`mb-0 ${isFullyPaid() ? 'text-success' : 'text-warning'}`}>
                               {calculateRemainingAmount()}
                            </h4>
                        </Alert>
                    </Col>
                </Row>

                {/* Existing Payments Table */}
                {order?.payments && order.payments.length > 0 && (
                    <>
                        <h6 className="mt-3 mb-2">Existing Payments</h6>
                        <div className="table-responsive mb-3">
                            <table className="table table-sm table-bordered">
                                <thead className="table-light">
                                    <tr>
                                        <th>Payment Method</th>
                                        <th>Amount</th>
                                        <th>Payment Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {order.payments.map((payment, idx) => (
                                        <tr key={idx}>
                                            <td>{payment.payment_method || 'N/A'}</td>
                                            <td className="text-success fw-bold">{parseFloat(payment.amount)}</td>
                                            <td>{formatDate(payment.payment_date)}</td>
                                        </tr>
                                    ))}
                                    <tr className="table-secondary">
                                        <td className="fw-bold">Total Paid</td>
                                        <td className="fw-bold text-success">
                                            {getExistingTotalPaid()}
                                        </td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </>
                )}

                {errors.total_exceeds && (
                    <Alert variant="danger" className="mb-3">
                        {errors.total_exceeds}
                    </Alert>
                )}

                {isFullyPaid() ? (
                    <Alert variant="success" className="mb-3 text-center">
                        <strong>✓ Order is fully paid!</strong> No more payments can be added.
                    </Alert>
                ) : (
                    <>
                        <h6 className="mt-3 mb-2">Add New Payments</h6>
                        <Form>
                            <div className="table-responsive">
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '35%' }}>Payment Method</th>
                                            <th style={{ width: '30%' }}>
                                                Amount
                                            </th>
                                            <th style={{ width: '30%' }}>Payment Date</th>
                                            <th style={{ width: '5%' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.map((payment, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <Form.Select
                                                        value={payment.payment_method_id}
                                                        onChange={(e) => updatePayment(index, 'payment_method_id', e.target.value)}
                                                        isInvalid={!!errors[`payments.${index}.payment_method_id`]}
                                                        size="sm"
                                                        disabled={isFullyPaid()}
                                                    >
                                                        <option value="">Select Method</option>
                                                        {paymentMethods.map(method => (
                                                            <option key={method.id} value={method.id}>
                                                                {method.name}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    {errors[`payments.${index}.payment_method_id`] && (
                                                        <Form.Text className="text-danger d-block">
                                                            {errors[`payments.${index}.payment_method_id`]}
                                                        </Form.Text>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        <Form.Control
                                                            type="number"
                                                            step="0.01"
                                                            value={payment.amount}
                                                            onChange={(e) => updatePayment(index, 'amount', e.target.value)}
                                                            isInvalid={!!errors[`payments.${index}.amount`]}
                                                            placeholder="0.00"
                                                            size="sm"
                                                            disabled={isFullyPaid()}
                                                        />
                                                    </div>
                                                    {errors[`payments.${index}.amount`] && (
                                                        <Form.Text className="text-danger d-block">
                                                            {errors[`payments.${index}.amount`]}
                                                        </Form.Text>
                                                    )}
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="date"
                                                        value={payment.payment_date}
                                                        onChange={(e) => updatePayment(index, 'payment_date', e.target.value)}
                                                        isInvalid={!!errors[`payments.${index}.payment_date`]}
                                                        size="sm"
                                                        disabled={isFullyPaid()}
                                                    />
                                                    {errors[`payments.${index}.payment_date`] && (
                                                        <Form.Text className="text-danger d-block">
                                                            {errors[`payments.${index}.payment_date`]}
                                                        </Form.Text>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => removePaymentRow(index)}
                                                        disabled={payments.length === 1 || isFullyPaid()}
                                                    >
                                                        <FiX />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="table-light">
                                        <tr>
                                            <td colSpan="4">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={addPaymentRow}
                                                    disabled={isFullyPaid() || calculateRemainingAmount() === 0}
                                                >
                                                    <FiPlus className="me-1" /> Add Another Payment
                                                </Button>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan="2" className="text-end fw-bold">New Payments Total:</td>
                                            <td colSpan="2" className="fw-bold text-primary">
                                                {calculateNewPaymentsTotal()}
                                            </td>
                                        </tr>
                                        <tr className="table-success">
                                            <td colSpan="2" className="text-end fw-bold">Grand Total:</td>
                                            <td colSpan="2" className="fw-bold text-success">
                                                {calculateTotalPaid()}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </Form>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="p-2">
                <Button variant="secondary" onClick={onClose} disabled={submitting}>
                    Close
                </Button>
                {!isFullyPaid() && (
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled() || submitting}
                    >
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Processing...
                            </>
                        ) : (
                            'Add Payment'
                        )}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

// Action Buttons Component
const ActionButtons = ({ order, onView, onEdit, onDelete, onInvoicePrint, onAddPayment }) => {
    const { t } = useContext(TranslatorContext);

    return (
        <div className="d-flex gap-2 justify-content-center">
            <Button
                variant="info"
                size="sm"
                onClick={() => onView(order)}
                title={t("View Details")}
            >
                <FiEye size={14} />
            </Button>
            <Button
                variant="success"
                size="sm"
                onClick={() => onAddPayment(order)}
                title={t("Add Payment")}
            >
                <FiCreditCard size={14} />
            </Button>
        </div>
    );
};

// Main Order Index Page
export default function OrderIndexPage() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const AuthToken = localStorage.getItem("authToken");

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingOrder, setViewingOrder] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);

    // Filter states
    const [tempFilters, setTempFilters] = useState({
        status: '',
        search: '',
        start_date: '',
        end_date: '',
        per_page: 10
    });

    const [activeFilters, setActiveFilters] = useState({
        status: '',
        search: '',
        start_date: '',
        end_date: '',
        per_page: 10
    });

    // Pagination states
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0
    });

    useEffect(() => {
        fetchOrders();
    }, [activeFilters.status, activeFilters.search, activeFilters.start_date, activeFilters.end_date, activeFilters.per_page, pagination.current_page]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: pagination.current_page,
                per_page: activeFilters.per_page,
                ...(activeFilters.status && { status: activeFilters.status }),
                ...(activeFilters.search && { search: activeFilters.search }),
                ...(activeFilters.start_date && { start_date: activeFilters.start_date }),
                ...(activeFilters.end_date && { end_date: activeFilters.end_date })
            });

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/orders?${params}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            setOrders(response.data.orders.data);
            setPaymentMethods(response.data.payment_methods || []);
            setPagination({
                current_page: response.data.orders.current_page || 1,
                last_page: response.data.orders.last_page || 1,
                per_page: response.data.orders.per_page || 10,
                total: response.data.orders.total || 0,
                from: response.data.orders.from || 0,
                to: response.data.orders.to || 0
            });
        } catch (err) {
            console.error('Error fetching orders:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch orders',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setTempFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applySearch = () => {
        setActiveFilters({
            status: tempFilters.status,
            search: tempFilters.search,
            start_date: tempFilters.start_date,
            end_date: tempFilters.end_date,
            per_page: tempFilters.per_page
        });
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    const clearFilters = () => {
        const emptyFilters = {
            status: '',
            search: '',
            start_date: '',
            end_date: '',
            per_page: 10
        };
        setTempFilters(emptyFilters);
        setActiveFilters(emptyFilters);
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) {
            setPagination(prev => ({
                ...prev,
                current_page: page
            }));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handlePerPageChange = (e) => {
        const value = parseInt(e.target.value);
        setTempFilters(prev => ({
            ...prev,
            per_page: value
        }));
        setActiveFilters(prev => ({
            ...prev,
            per_page: value
        }));
        setPagination(prev => ({
            ...prev,
            current_page: 1
        }));
    };

    const handleStatusUpdate = (orderId, newStatus) => {
        setOrders(prevOrders =>
            prevOrders.map(order =>
                order.id === orderId
                    ? { ...order, status: newStatus }
                    : order
            )
        );

        if (viewingOrder && viewingOrder.id === orderId) {
            setViewingOrder({ ...viewingOrder, status: newStatus });
        }
    };

    const handleViewClick = (order) => {
        setViewingOrder(order);
        setShowViewModal(true);
    };

    const handleViewDetails = (id) => {
        navigate(`/orders/${id}`);
    };

    const handleAddPayment = (order) => {
        setSelectedOrder(order);
        setShowPaymentModal(true);
    };

    const handlePaymentSuccess = () => {
        // Refresh orders to show updated payment information
        fetchOrders();
    };

    const renderBootstrapPagination = () => {
        let items = [];
        let maxVisible = 5;
        let startPage = Math.max(1, pagination.current_page - Math.floor(maxVisible / 2));
        let endPage = Math.min(pagination.last_page, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        items.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
            />
        );

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    {1}
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.current_page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < pagination.last_page) {
            if (endPage < pagination.last_page - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.last_page}
                    onClick={() => handlePageChange(pagination.last_page)}
                >
                    {pagination.last_page}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.last_page}
            />
        );

        return items;
    };

    return (
        <PageLayout>
            <div className="order-index-page">
                {/* Sticky Header */}
                <div className="sticky-header p-3 bg-white shadow-sm mb-4 rounded">
                    <Row className="align-items-center">
                        <Col md={6}>
                            <h2 className="mb-0 fw-bold">
                                <FiShoppingCart className="me-2 text-primary" />
                                {t("Orders Management")}
                            </h2>
                            <p className="text-muted mt-2 mb-0">Manage all customer orders</p>
                        </Col>
                    </Row>
                </div>

                {/* Filters Section */}
                <Card className="shadow-sm mb-4">
                    <Card.Body>
                        <Row>
                            <Col md={4} className="mb-3">
                                <Form.Group>
                                    <Form.Label className="small fw-bold">{t("Search")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="search"
                                        size="sm"
                                        placeholder="Order number, customer name, email or phone..."
                                        value={tempFilters.search}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3} className="mb-3">
                                <Form.Group>
                                    <Form.Label className="small fw-bold">{t("Status")}</Form.Label>
                                    <Form.Select
                                        name="status"
                                        size="sm"
                                        value={tempFilters.status}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">{t("All Status")}</option>
                                        {STATUS_OPTIONS.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>

                            <Col md={2} className="mb-3">
                                <Form.Group>
                                    <Form.Label className="small fw-bold">{t("Start Date")}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="start_date"
                                        size="sm"
                                        value={tempFilters.start_date}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2} className="mb-3">
                                <Form.Group>
                                    <Form.Label className="small fw-bold">{t("End Date")}</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="end_date"
                                        size="sm"
                                        value={tempFilters.end_date}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={3} className="mt-1">
                                <div className="d-flex gap-2">
                                    <Button variant="primary" size="m" onClick={applySearch}>
                                        <FiSearch className="me-1" /> {t("Search")}
                                    </Button>
                                    <Button variant="danger" size="sm" onClick={clearFilters}>
                                        <FiX className="me-1" /> {t("Clear All")}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <Card className="shadow-sm">
                            <Card.Body className="p-0">
                                <div className="table-responsive-wrapper">
                                    <Table responsive hover className="table mb-0">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '12%' }}>{t("Order Number")}</th>
                                                <th style={{ width: '15%' }}>{t("Customer")}</th>
                                                <th style={{ width: '20%' }}>{t("Contact")}</th>
                                                <th style={{ width: '20%' }}>{t("Address")}</th>
                                                <th style={{ width: '10%' }}>{t("Order Date")}</th>
                                                <th style={{ width: '10%' }}>{t("Total Amount")}</th>
                                                <th style={{ width: '13%' }}>{t("Status")}</th>
                                                <th style={{ width: '15%' }} className="text-center">{t("Actions")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orders.length > 0 ? (
                                                orders.map((order) => (
                                                    <tr key={order.id}>
                                                        <td>
                                                            <strong>
                                                                {order.order_number}
                                                            </strong>
                                                        </td>
                                                        <td>{order.name || '—'}</td>
                                                        <td>
                                                            <div className="small">
                                                                <div><FiMail className="me-1" size={12} /> {order.email || '—'}</div>
                                                                <div><FiPhone className="me-1" size={12} /> {order.phone || '—'}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="small">
                                                                <div>{order.address?.substring(0, 30)}...</div>
                                                            </div>
                                                        </td>
                                                        <td>{formatDate(order.order_date)}</td>
                                                        <td>
                                                            <strong className="text-success">
                                                                {parseFloat(order.total_amount) || '0.00'}
                                                            </strong>
                                                        </td>
                                                        <td>
                                                            <StatusBadge
                                                                status={order.status}
                                                                onStatusChange={handleStatusUpdate}
                                                                orderId={order.id}
                                                            />
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <Button
                                                                    variant="info"
                                                                    size="sm"
                                                                    onClick={() => handleViewDetails(order.id)}
                                                                    title={t("View Details")}
                                                                >
                                                                    <FiEye size={14} />
                                                                </Button>
                                                                <Button
                                                                    variant="success"
                                                                    size="sm"
                                                                    onClick={() => handleAddPayment(order)}
                                                                    title={t("Add Payment")}
                                                                >
                                                                    <FiCreditCard size={14} />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center py-5">
                                                        <div className="empty-state">
                                                            <FiShoppingCart size={48} className="text-muted mb-2" />
                                                            <p className="text-muted mb-0">{t("No orders found")}</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Pagination Section */}
                        {pagination.last_page > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top flex-wrap gap-2">
                                <div className="text-muted small">
                                    {t('Showing')} {pagination.from} {t('to')} {pagination.to} {t('of')} {pagination.total} {t('entries')}
                                </div>
                                <Pagination className="mb-0">
                                    {renderBootstrapPagination()}
                                </Pagination>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Payment Modal */}
            <PaymentModal
                show={showPaymentModal}
                onClose={() => {
                    setShowPaymentModal(false);
                    setSelectedOrder(null);
                }}
                order={selectedOrder}
                onPaymentSuccess={handlePaymentSuccess}
                paymentMethods={paymentMethods}
            />
        </PageLayout>
    );
}